import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import TriviaImage from '@/assets/icons/cover-images/TRIVIA_blue.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { getOptimalFontSize } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';
import VariationsStyles from './VariationsStyles';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariations = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          async () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var22');

            // Wait for font to load before measuring text
            await document.fonts.ready;

            const leftImage = new Image();
            leftImage.src = variation.leftImageSrc;
            leftImage.classList.add('half-image-var22');
            imageHolder.appendChild(leftImage);

            const leftWatermark = createWatermarkTagImage(
              'actual-photo-var22',
              variation.leftWatermarkTag,
              variation.leftWatermarkPosition,
            );
            if (leftWatermark) imageHolder.appendChild(leftWatermark);

            const middleBreak = document.createElement('div');

            middleBreak.classList.add('break-var22');

            imageHolder.appendChild(middleBreak);

            const rightHolder = document.createElement('div');
            rightHolder.classList.add('right-holder-var22');
            const rightImageHolder = document.createElement('div');
            rightImageHolder.classList.add('right-image-holder-var22');
            const triviaIcon = new Image();
            triviaIcon.src = TriviaImage;
            triviaIcon.classList.add('trivia-icon-var22');
            rightImageHolder.appendChild(triviaIcon);
            rightHolder.appendChild(rightImageHolder);
            imageHolder.appendChild(rightHolder);

            if (variation.rightText) {
              const rightText = document.createElement('div');
              rightText.classList.add('right-text-var22');

              let text = '';
              const updatedText = replaceQuotes(variation.rightText);
              text = updatedText;

              const algorithmResult = getOptimalFontSize(text, {
                maxWidth: 480,
                maxHeight: 320,
                fontFamily: 'CenturyGothicBold',
                letterSpacing: '0.7px',
                minFontSize: 45,
                maxFontSize: 120,
                lineHeight: '1.2',
              });

              if (variation.brakes) {
                rightText.innerText = text;
              } else {
                rightText.innerHTML = algorithmResult.lines.join('<br/>');
              }

              if (variation.fontSize) {
                rightText.style.fontSize = `${variation.fontSize}px`;
              } else {
                rightText.style.fontSize = `${algorithmResult.fontSize}px`;
              }

              rightHolder.appendChild(rightText);
            }

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var22',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariations,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <VariationsStyles />
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
